import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Seo } from "@/components/Seo";
import { api } from "@/lib/api";
import { ArrowLeft, Printer, ShieldCheck } from "lucide-react";
import dayjs from "dayjs";

// Phase 17-D item 8: print-styled DofE/UCAS evidence pack. Contents are the
// immutable snapshot taken at pack creation; the code verifies at /verify.
export default function EvidencePackPage() {
  const { id } = useParams();
  const [pack, setPack] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.getEvidencePack(id).then(setPack).catch((e) => setErr(e?.response?.status === 403 ? "This isn't your evidence pack." : "Pack not found."));
  }, [id]);

  if (err) return <div className="max-w-3xl mx-auto px-6 py-20 text-neutral-500">{err}</div>;
  if (!pack) return <div className="max-w-3xl mx-auto px-6 py-20 text-neutral-500">Loading…</div>;

  const verifyUrl = `${window.location.origin}/verify/${pack.code}`;

  return (
    <div className="max-w-3xl mx-auto px-6 py-10" data-testid="evidence-pack-page">
      <Seo title="Evidence pack" />
      <div className="no-print flex items-center justify-between">
        <Link to="/recognition?tab=evidence" className="text-sm text-neutral-500 hover:text-forest inline-flex items-center gap-1.5">
          <ArrowLeft className="w-4 h-4" /> Back to Recognition
        </Link>
        <Button data-testid="evidence-pack-print-btn" onClick={() => window.print()} className="rounded-full bg-forest hover:bg-forest-hover">
          <Printer className="w-4 h-4 mr-2" /> Print pack
        </Button>
      </div>

      <div className="certificate-print mt-8 bg-white border border-neutral-200 rounded-2xl p-10">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-400">
              Civic<span className="text-terracotta">.</span>Hearth — volunteering evidence pack
            </div>
            <h1 className="font-display text-3xl text-forest mt-2 tracking-tight" data-testid="evidence-pack-volunteer">{pack.volunteer_display}</h1>
            <div className="text-sm text-neutral-600 mt-1">
              {dayjs(pack.from_date).format("D MMM YYYY")} — {dayjs(pack.to_date).format("D MMM YYYY")}
            </div>
          </div>
          <div className="text-right">
            <div className="font-display text-3xl text-forest" data-testid="evidence-pack-total">{pack.total_hours}h</div>
            <div className="text-[11px] uppercase tracking-[0.16em] text-forest/60 font-bold">approved hours</div>
          </div>
        </div>

        <table className="w-full mt-8 text-xs" data-testid="evidence-pack-table">
          <thead>
            <tr className="text-left text-neutral-400 uppercase tracking-wide">
              <th className="py-1.5 pr-3 font-medium">Date</th>
              <th className="py-1.5 pr-3 font-medium">Organisation</th>
              <th className="py-1.5 pr-3 font-medium">Activity</th>
              <th className="py-1.5 text-right font-medium">Hours</th>
            </tr>
          </thead>
          <tbody>
            {pack.entries.map((e, i) => (
              <tr key={i} className="border-t border-neutral-100 align-top">
                <td className="py-2 pr-3 whitespace-nowrap text-neutral-600">{dayjs(e.date).format("D MMM YY")}</td>
                <td className="py-2 pr-3 text-forest">{e.org_name}</td>
                <td className="py-2 pr-3 text-neutral-700">
                  {e.opportunity_title}
                  {e.narrative && <div className="text-neutral-500 italic mt-0.5">“{e.narrative}”</div>}
                </td>
                <td className="py-2 text-right font-medium text-forest">{e.hours}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-8" data-testid="evidence-pack-orgs">
          <div className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">Verifying organisations</div>
          {pack.org_lines.map((o) => (
            <div key={o.org_user_id} className="text-sm text-neutral-700 mt-1.5">
              {o.org_name}{o.email ? ` — ${o.email}` : ""}
            </div>
          ))}
        </div>

        <div className="mt-8 pt-5 border-t border-neutral-200 flex items-start justify-between flex-wrap gap-3">
          <div className="text-xs text-neutral-500 max-w-sm">
            <ShieldCheck className="w-4 h-4 text-green-600 inline mr-1.5 -mt-0.5" />
            Hours logged by the volunteer and approved by the organisations named above.
            Verify this pack's authenticity at any time:
          </div>
          <div className="text-right">
            <div className="font-mono text-sm text-forest" data-testid="evidence-pack-code">{pack.code}</div>
            <div className="text-[11px] text-neutral-500">{verifyUrl.replace(/^https?:\/\//, "")}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
